import { CalendarOutlined, CarOutlined, InfoCircleOutlined, UserOutlined } from "@ant-design/icons";
import { Tag } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
    formatCurrency,
    getCarSaleLabel,
    getLocationLabel,
    PROVIDER_LABELS,
} from "../../../lib/insuranceSaleApi";
import InsuranceSalePaymentFields from "./InsuranceSalePaymentFields";

const formatDateTime = (value) => (value ? dayjs(value).format("MMM D, YYYY h:mm A") : "—");

const ReviewSection = ({ icon, title, children }) => (
    <section className="border-b border-gray-100 px-4 py-4 last:border-b-0 sm:px-5">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-400">
            <span className="text-base text-[#5D5DF6]">{icon}</span>
            {title}
        </h4>
        {children}
    </section>
);

ReviewSection.propTypes = {
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const ReviewField = ({ label, children }) => (
    <div className="min-w-0">
        <dt className="text-sm text-gray-500">{label}</dt>
        <dd className="m-0 mt-0.5 break-words text-base font-medium text-[#2F2F46]">{children}</dd>
    </div>
);

ReviewField.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

const PriceRow = ({ label, value, strong }) => (
    <div className="flex items-baseline justify-between gap-3">
        <span className={strong ? "text-base font-semibold text-gray-900" : "text-base text-gray-500"}>{label}</span>
        <span className={strong ? "text-2xl font-bold text-gray-900" : "text-base font-medium text-[#2F2F46]"}>
            {value}
        </span>
    </div>
);

PriceRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    strong: PropTypes.bool,
};

PriceRow.defaultProps = {
    strong: false,
};

const ReviewSaleStep = ({
    provider,
    customer,
    car,
    location,
    quote,
    stripeLoading,
    sendLinkLoading,
    sendLinkDisabled,
    sendLinkDisabledReason,
    onStripePay,
    onSendLink,
}) => {
    const { t } = useTranslation("sellInsurance");
    const totalDue = Number(quote?.total ?? 0);
    const isLinked = car?.insuranceVehicleInfo?.active === true;
    const imageUrl = car?.images?.image1;
    const customerName = `${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim() || "—";

    return (
        <div className="grid min-w-0 grid-cols-1 gap-4 pb-2 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
            <div className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white">
                <ReviewSection icon={<UserOutlined />} title={t("wizard.stepCustomer")}>
                    <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <ReviewField label={t("wizard.customerName")}>{customerName}</ReviewField>
                        <ReviewField label={t("wizard.provider")}>{PROVIDER_LABELS[provider] ?? "—"}</ReviewField>
                        {customer?.email ? (
                            <ReviewField label={t("wizard.email")}>
                                <span className="break-all">{customer.email}</span>
                            </ReviewField>
                        ) : null}
                        {customer?.mobileNumber ? (
                            <ReviewField label={t("wizard.phone")}>{customer.mobileNumber}</ReviewField>
                        ) : null}
                    </dl>
                </ReviewSection>

                <ReviewSection icon={<CarOutlined />} title={t("wizard.vehicle")}>
                    <div className="flex items-center gap-3">
                        <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                            {imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt={car ? getCarSaleLabel(car) : ""}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400">
                                    {t("wizard.noCarImage")}
                                </div>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="m-0 truncate text-base font-semibold text-gray-900">
                                {car ? getCarSaleLabel(car) : "—"}
                            </p>
                            <p className="m-0 mt-0.5 break-all font-mono text-sm text-gray-500">{car?.VIN || "—"}</p>
                        </div>
                        {car ? (
                            <Tag color={isLinked ? "success" : "warning"} className="m-0 shrink-0 rounded-full">
                                {isLinked ? t("wizard.insuranceLinked") : t("wizard.insuranceNotLinked")}
                            </Tag>
                        ) : null}
                    </div>
                    <dl className="m-0 mt-3">
                        <ReviewField label={t("wizard.pickupLocation")}>
                            {location ? getLocationLabel(location) : "—"}
                        </ReviewField>
                    </dl>
                </ReviewSection>

                <ReviewSection icon={<CalendarOutlined />} title={t("wizard.coveragePeriod")}>
                    <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <ReviewField label={t("wizard.coverageStart")}>{formatDateTime(quote?.pickupDate)}</ReviewField>
                        <ReviewField label={t("wizard.coverageEnd")}>{formatDateTime(quote?.dropoffDate)}</ReviewField>
                        <ReviewField label={t("wizard.product")}>{quote?.product?.name ?? "—"}</ReviewField>
                    </dl>
                </ReviewSection>
            </div>

            <aside className="min-w-0 rounded-xl border border-[#D7D6FE] bg-[#F7F7FF] p-4 sm:p-5 lg:sticky lg:top-0">
                <h4 className="mb-3 text-base font-semibold text-gray-900">{t("wizard.priceSummary")}</h4>
                <div className="space-y-2">
                    <PriceRow
                        label={`${t("wizard.dailyRate")} × ${quote?.billableDays ?? 0}`}
                        value={formatCurrency(quote?.dailyRate)}
                    />
                    <PriceRow label={t("wizard.taxes")} value={formatCurrency(quote?.taxTotal)} />
                </div>
                <div className="my-3 border-t border-dashed border-[#D7D6FE]" />
                <PriceRow label={t("wizard.total")} value={formatCurrency(quote?.total)} strong />

                <p className="mb-4 mt-3 flex gap-2 text-sm leading-relaxed text-gray-500">
                    <InfoCircleOutlined className="mt-0.5 shrink-0 text-[#5D5DF6]" />
                    {t("wizard.reviewNotice")}
                </p>

                <InsuranceSalePaymentFields
                    amountDue={totalDue}
                    showAmountDue={false}
                    showStripeButton
                    stripeLoading={stripeLoading}
                    onStripePay={onStripePay}
                    showSendLinkButton
                    sendLinkLoading={sendLinkLoading}
                    sendLinkDisabled={sendLinkDisabled}
                    sendLinkDisabledReason={sendLinkDisabledReason}
                    onSendLink={onSendLink}
                />
            </aside>
        </div>
    );
};

ReviewSaleStep.propTypes = {
    provider: PropTypes.string,
    customer: PropTypes.object,
    car: PropTypes.object,
    location: PropTypes.object,
    quote: PropTypes.object,
    stripeLoading: PropTypes.bool,
    sendLinkLoading: PropTypes.bool,
    sendLinkDisabled: PropTypes.bool,
    sendLinkDisabledReason: PropTypes.string,
    onStripePay: PropTypes.func,
    onSendLink: PropTypes.func,
};

ReviewSaleStep.defaultProps = {
    provider: undefined,
    customer: null,
    car: null,
    location: null,
    quote: null,
    stripeLoading: false,
    sendLinkLoading: false,
    sendLinkDisabled: false,
    sendLinkDisabledReason: undefined,
    onStripePay: undefined,
    onSendLink: undefined,
};

export default ReviewSaleStep;
